import React from "react";
import Seo from "@/components/Seo";
import ServiceHero from "@/components/marketing/service/ServiceHero";
import KnowledgePanelFeature from "@/components/marketing/service/reputation/KnowledgePanelFeature";
import KeywordScope from "@/components/marketing/service/reputation/KeywordScope";
import ReputationServices from "@/components/marketing/service/reputation/ReputationServices";
import HowItWorksLedger from "@/components/marketing/service/HowItWorksLedger";
import ServiceFAQ from "@/components/marketing/service/ServiceFAQ";
import CTASection from "@/components/marketing/CTASection";
import Reveal from "@/components/marketing/Reveal";

const RM_STEPS = [
  { title: "We audit your search presence", desc: "We map what currently ranks for your name and brand — page one, AI answers, and Knowledge Panel status." },
  { title: "We map your personal & brand keywords", desc: "Together we agree the exact keywords and entities we're building around." },
  { title: "We deploy the right D24 services", desc: "Guaranteed placements, newswire, and journalist outreach — each tailored to your keywords." },
  { title: "We build entity signals", desc: "Structured data, verified profiles, and the authoritative references that trigger a Knowledge Panel." },
  { title: "We monitor and reinforce", desc: "We track the results and reinforce with ongoing coverage so your reputation holds." },
];

const RM_FAQS = [
  { q: "What is reputation management?", a: "Reputation management is the practice of shaping what appears when people search for your name or brand. We tailor Digital24's PR and placement services to own those results with coverage you control." },
  { q: "What is a Google Knowledge Panel?", a: "A Knowledge Panel is the information box Google shows on the right of search results for notable people and organisations. It signals that Google recognises you as a distinct entity." },
  { q: "How much does a Knowledge Panel cost?", a: "Our Knowledge Panel creation service is $2,500 per engagement. We build the entity signals, authoritative references, and verified profiles Google needs to generate the panel." },
  { q: "How long does reputation management take?", a: "Placements and press can move results within weeks. Knowledge Panel creation and AI search visibility are longer-term plays that build over months as Google processes the new entity signals." },
  { q: "Is a Knowledge Panel guaranteed?", a: "Google ultimately decides whether to generate a panel, so it cannot be guaranteed. We build the strongest possible entity signals to maximise the likelihood, and reinforce them until a panel appears." },
  { q: "Which services are included?", a: "We tailor Guaranteed Placements, Newswire Distribution, and Journalist Outreach around your keywords. The mix depends on your goals and current search presence — contact us for a tailored plan." },
];

export default function ReputationManagement() {
  return (
    <div className="bg-canvas">
      <Seo title="Reputation Management & Knowledge Panels | Digital24" />
      <ServiceHero
        eyebrow="REPUTATION MANAGEMENT"
        title="Own the search results for your name and brand."
        subtitle="We tailor every Digital24 service around your personal and brand keywords — so the results people find are the ones you control, and Google recognises you as an entity worth a Knowledge Panel."
        primaryCTA={{ label: "Contact Us for More Info", to: "/contact" }}
        secondaryCTA={{ label: "See the Knowledge Panel Service", to: "#knowledge-panel" }}
      />
      <Reveal variant="card">
        <KnowledgePanelFeature />
      </Reveal>
      <Reveal variant="card">
        <KeywordScope />
      </Reveal>
      <Reveal variant="card">
        <ReputationServices />
      </Reveal>
      <HowItWorksLedger
        steps={RM_STEPS}
        tag="HOW IT WORKS"
        title="How we manage your reputation"
      />
      <ServiceFAQ
        faqs={RM_FAQS}
        tag="FAQ"
        title="Reputation management FAQ"
      />
      <CTASection
        eyebrow="TALK TO US"
        headline="Let's shape what people find when they search for you."
        subtitle="Tell us your name, your brand, and your goals — we'll build a reputation management plan around your keywords, including a Knowledge Panel if you need one."
        primaryTo="/contact"
      />
    </div>
  );
}